/**
 * AUA HYDRATE — Cart drawer & AJAX cart
 * Uses the Shopify Cart Ajax API to add/update/remove items and re-render the drawer.
 *
 * Endpoints used:
 *   POST /cart/add.js
 *   POST /cart/change.js
 *   GET  /cart.js
 *   GET  /?section_id=cart-drawer-section  (if available — falls back to /cart.js)
 *
 * Public events emitted on document:
 *   - aua:cart:add { item, cart }
 *   - aua:cart:update { cart }
 *   - aua:cart:open
 *   - aua:cart:close
 */

(() => {
  if (typeof window === 'undefined') return;

  // ============================================
  // I18N (asset JS is not Liquid-processed — detect storefront language
  // from <html lang> and pick the matching UI strings)
  // ============================================
  const AUA_LANG = (document.documentElement.getAttribute('lang') || 'fr').slice(0, 2).toLowerCase();
  const AUA_I18N = {
    fr: {"shipDone": "Livraison offerte appliquée.", "shipPrefix": "Plus que ", "shipSuffix": " pour la livraison offerte.", "addError": "Erreur ajout au panier", "empty": "Votre panier est vide.", "discover": "Découvrir les arômes", "decrease": "Diminuer", "increase": "Augmenter", "remove": "Retirer", "noteAdd": "Ajouter une note", "noteOptional": "facultatif", "noteAria": "Note pour la commande (facultatif)", "notePlaceholder": "Une précision pour nos préparateurs ? (ex. instructions de livraison)", "subtotal": "Sous-total", "taxesNote": "Livraison et taxes calculées au paiement.", "viewCart": "Voir le panier", "checkout": "Passer commande", "adding": "Ajout en cours…", "addFail": "Impossible d'ajouter au panier. Réessayez."},
    de: {"shipDone": "Kostenloser Versand aktiviert.", "shipPrefix": "Nur noch ", "shipSuffix": " bis zum kostenlosen Versand.", "addError": "Fehler beim Hinzufügen zum Warenkorb", "empty": "Ihr Warenkorb ist leer.", "discover": "Sorten entdecken", "decrease": "Verringern", "increase": "Erhöhen", "remove": "Entfernen", "noteAdd": "Notiz hinzufügen", "noteOptional": "optional", "noteAria": "Notiz zur Bestellung (optional)", "notePlaceholder": "Ein Hinweis für unser Team? (z. B. Lieferhinweise)", "subtotal": "Zwischensumme", "taxesNote": "Versand und Steuern werden an der Kasse berechnet.", "viewCart": "Warenkorb ansehen", "checkout": "Zur Kasse", "adding": "Wird hinzugefügt…", "addFail": "Hinzufügen zum Warenkorb nicht möglich. Bitte erneut versuchen."},
    it: {"shipDone": "Spedizione gratuita applicata.", "shipPrefix": "Ancora ", "shipSuffix": " per la spedizione gratuita.", "addError": "Errore durante l'aggiunta al carrello", "empty": "Il tuo carrello è vuoto.", "discover": "Scopri i gusti", "decrease": "Riduci", "increase": "Aumenta", "remove": "Rimuovi", "noteAdd": "Aggiungi una nota", "noteOptional": "facoltativo", "noteAria": "Nota per l'ordine (facoltativo)", "notePlaceholder": "Una precisazione per il nostro team? (es. istruzioni di consegna)", "subtotal": "Subtotale", "taxesNote": "Spedizione e tasse calcolate al momento del pagamento.", "viewCart": "Vedi il carrello", "checkout": "Procedi all'ordine", "adding": "Aggiunta in corso…", "addFail": "Impossibile aggiungere al carrello. Riprova."}
  };
  const T = AUA_I18N[AUA_LANG] || AUA_I18N.fr;

  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    open: '[data-cart-open]',
    close: '[data-drawer-close]',
    items: '[data-cart-items]',
    item: '[data-cart-item]',
    qtyValue: '[data-qty-value]',
    qtyBtn: '[data-cart-qty]',
    remove: '[data-cart-remove]',
    body: '[data-cart-body]',
    footer: '[data-cart-footer]',
    total: '[data-cart-total]',
    empty: '[data-cart-empty]',
    productForm: 'form[action*="/cart/add"]',
    headerCount: '.aua-header__cart-count',
    headerCart: '.aua-header__cart',
  };

  const moneyFormatter = (cents) => {
    const n = (parseInt(cents, 10) / 100).toFixed(2).replace('.', ',');
    return 'CHF ' + n;
  };

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  // Free-shipping threshold (cents). Keep in sync with cart-drawer.liquid.
  const FREE_SHIPPING_THRESHOLD = 6000;

  const updateShipBar = (totalCents) => {
    const bar = document.querySelector('[data-ship-bar]');
    if (!bar) return;
    const total = parseInt(totalCents, 10) || 0;
    const reached = total >= FREE_SHIPPING_THRESHOLD;
    bar.dataset.complete = reached ? 'true' : 'false';
    const pct = Math.min(100, (total / FREE_SHIPPING_THRESHOLD) * 100);
    const fill = bar.querySelector('[data-ship-bar-fill]');
    if (fill) fill.style.width = pct + '%';
    const track = bar.querySelector('.aua-ship-bar__track');
    if (track) track.setAttribute('aria-valuenow', Math.round(pct));
    const text = bar.querySelector('[data-ship-bar-text]');
    if (text) {
      if (reached) {
        text.textContent = T.shipDone;
      } else {
        const remaining = FREE_SHIPPING_THRESHOLD - total;
        text.innerHTML = T.shipPrefix + '<strong>' + moneyFormatter(remaining) + '</strong>' + T.shipSuffix;
      }
    }
  };

  // ============================================
  // DRAWER OPEN / CLOSE
  // ============================================
  let lastFocused = null;

  const openDrawer = () => {
    const drawer = $(SELECTORS.drawer);
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(() => {
      drawer.setAttribute('data-open', '');
      drawer.setAttribute('scroll-lock', '');
    });
    document.dispatchEvent(new CustomEvent('aua:cart:open'));
    // Move focus to close button for keyboard users
    const closeBtn = $(SELECTORS.close, drawer);
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  };

  const closeDrawer = () => {
    const drawer = $(SELECTORS.drawer);
    if (!drawer) return;
    drawer.removeAttribute('data-open');
    drawer.removeAttribute('scroll-lock');
    setTimeout(() => {
      drawer.hidden = true;
      if (lastFocused && lastFocused.focus) lastFocused.focus({ preventScroll: true });
    }, 500);
    document.dispatchEvent(new CustomEvent('aua:cart:close'));
  };

  // Bind triggers
  document.addEventListener('click', (e) => {
    const opener = e.target.closest(SELECTORS.open);
    const closer = e.target.closest(SELECTORS.close);
    const cartLink = e.target.closest(SELECTORS.headerCart);

    if (opener || (cartLink && !e.metaKey && !e.ctrlKey)) {
      e.preventDefault();
      openDrawer();
      return;
    }
    if (closer) {
      e.preventDefault();
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const drawer = $(SELECTORS.drawer);
      if (drawer && drawer.hasAttribute('data-open')) closeDrawer();
    }
  });

  // ============================================
  // CART API
  // ============================================
  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  };

  const addToCart = async (formData) => {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || T.addError);
    }
    return res.json();
  };

  const changeLine = async (line, quantity) => {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    return res.json();
  };

  // Persist the order note as a cart-level attribute. Shopify carries this
  // through to the order, where it is visible to order preparers in the admin.
  const updateNote = async (note) => {
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    });
    return res.json();
  };

  // ============================================
  // RENDER
  // ============================================
  const renderDrawer = (cart) => {
    const drawer = $(SELECTORS.drawer);
    if (!drawer) return;

    const body = $(SELECTORS.body, drawer);
    const footer = $(SELECTORS.footer, drawer);

    if (cart.item_count === 0) {
      // Empty state
      body.innerHTML = `
        <div class="aua-drawer__empty" data-cart-empty>
          <div class="aua-drawer__empty-mark aua-italic">aua.</div>
          <p>${T.empty}</p>
          <a href="/collections/tous-les-aromes" class="aua-btn aua-btn--secondary">${T.discover}</a>
        </div>`;
      if (footer) footer.remove();
      return;
    }

    // Item list
    const itemsHtml = cart.items.map((item, i) => {
      const line = i + 1;
      const imgUrl = item.image ? item.image.replace(/_(\d+)x\./, '_200x.') : null;
      const variantLine = item.product_has_only_default_variant
        ? ''
        : `<span class="aua-cart-item__variant">${escapeHtml(item.variant_title || '')}</span>`;
      return `
        <li class="aua-cart-item" data-cart-item data-line="${line}" data-key="${item.key}">
          <a href="${item.url}" class="aua-cart-item__image" aria-hidden="true">
            ${imgUrl ? `<img src="${imgUrl}" alt="" width="80" height="100" loading="lazy">` : `<div class="aua-cart-item__placeholder"></div>`}
          </a>
          <div class="aua-cart-item__body">
            <a href="${item.url}" class="aua-cart-item__title">${escapeHtml(item.product_title)}</a>
            ${variantLine}
            <div class="aua-cart-item__row">
              <div class="aua-cart-item__qty">
                <button type="button" class="aua-cart-item__qty-btn" data-cart-qty="-1" data-line="${line}" aria-label="${T.decrease}">−</button>
                <span class="aua-cart-item__qty-value" data-qty-value>${item.quantity}</span>
                <button type="button" class="aua-cart-item__qty-btn" data-cart-qty="1" data-line="${line}" aria-label="${T.increase}">+</button>
              </div>
              <span class="aua-cart-item__price">${moneyFormatter(item.final_line_price)}</span>
            </div>
            <button type="button" class="aua-cart-item__remove" data-cart-remove data-line="${line}">${T.remove}</button>
          </div>
        </li>`;
    }).join('');

    body.innerHTML = `<ul class="aua-cart-items" data-cart-items>${itemsHtml}</ul>`;

    // Rebuild footer if it doesn't exist
    if (!footer) {
      const panel = $('[data-drawer-panel]', drawer);
      panel.insertAdjacentHTML('beforeend', `
        <footer class="aua-drawer__footer" data-cart-footer>
          <div class="aua-ship-bar" data-ship-bar data-complete="false">
            <p class="aua-ship-bar__label">
              <svg class="aua-ship-bar__check" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 8l3 3 7-7" stroke-linecap="round" stroke-linejoin="round"/></svg>
              <span data-ship-bar-text>${T.shipPrefix}<strong>${moneyFormatter(FREE_SHIPPING_THRESHOLD)}</strong>${T.shipSuffix}</span>
            </p>
            <div class="aua-ship-bar__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
              <span class="aua-ship-bar__fill" data-ship-bar-fill style="width: 0%;"></span>
            </div>
          </div>
          <details class="aua-ordernote"${cart.note ? ' open' : ''}>
            <summary class="aua-ordernote__summary">
              <span class="aua-ordernote__label">${T.noteAdd} <span class="aua-ordernote__tag">${T.noteOptional}</span></span>
              <svg class="aua-ordernote__chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><path d="M4 6l4 4 4-4"/></svg>
            </summary>
            <textarea class="aua-ordernote__input" data-cart-note name="note" rows="3" maxlength="500" aria-label="${T.noteAria}" placeholder="${T.notePlaceholder}">${escapeHtml(cart.note)}</textarea>
          </details>
          <div class="aua-drawer__totals">
            <span class="aua-fineprint">${T.subtotal}</span>
            <span class="aua-drawer__total" data-cart-total>${moneyFormatter(cart.total_price)}</span>
          </div>
          <p class="aua-fineprint aua-drawer__note">${T.taxesNote}</p>
          <a href="/cart" class="aua-btn aua-btn--ghost aua-btn--sm">${T.viewCart}</a>
          <a href="/checkout" class="aua-btn aua-btn--primary aua-btn--lg" data-cart-checkout>
            ${T.checkout}
            <span class="aua-btn__icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 8h10M9 4l4 4-4 4"/></svg>
            </span>
          </a>
        </footer>`);
    } else {
      $(SELECTORS.total, footer).textContent = moneyFormatter(cart.total_price);
    }

    // Refresh the free-shipping progress bar (the footer exists by now).
    updateShipBar(cart.total_price);
  };

  const updateHeaderCount = (count) => {
    const headerCart = $(SELECTORS.headerCart);
    if (!headerCart) return;
    let badge = headerCart.querySelector(SELECTORS.headerCount);

    if (count > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'aua-header__cart-count';
        headerCart.appendChild(badge);
      }
      badge.textContent = count;
      badge.setAttribute('data-pulse', '');
      setTimeout(() => badge.removeAttribute('data-pulse'), 700);
    } else if (badge) {
      badge.remove();
    }
  };

  const escapeHtml = (str) => {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  };

  // ============================================
  // EVENT HANDLERS
  // ============================================

  // Quantity buttons inside drawer
  document.addEventListener('click', async (e) => {
    const qty = e.target.closest(SELECTORS.qtyBtn);
    const remove = e.target.closest(SELECTORS.remove);

    if (qty) {
      e.preventDefault();
      const line = parseInt(qty.dataset.line, 10);
      const delta = parseInt(qty.dataset.cartQty, 10);
      const valueEl = qty.parentElement.querySelector(SELECTORS.qtyValue);
      const current = parseInt(valueEl.textContent.trim(), 10);
      const next = Math.max(0, current + delta);
      const cart = await changeLine(line, next);
      renderDrawer(cart);
      updateHeaderCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('aua:cart:update', { detail: { cart } }));
    } else if (remove) {
      e.preventDefault();
      const line = parseInt(remove.dataset.line, 10);
      const cart = await changeLine(line, 0);
      renderDrawer(cart);
      updateHeaderCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('aua:cart:update', { detail: { cart } }));
    }
  });

  // ---- Order note (optional) ----
  let noteTimer = null;
  let noteDirty = false;

  const saveNoteFrom = async (textarea) => {
    if (!textarea) return;
    clearTimeout(noteTimer);
    try {
      await updateNote(textarea.value);
      noteDirty = false;
    } catch (err) {
      console.error('[AUA cart] note', err);
    }
  };

  // Debounced save while typing
  document.addEventListener('input', (e) => {
    const ta = e.target.closest('[data-cart-note]');
    if (!ta) return;
    noteDirty = true;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => saveNoteFrom(ta), 600);
  });

  // Immediate save when the field loses focus
  document.addEventListener('focusout', (e) => {
    const ta = e.target.closest('[data-cart-note]');
    if (!ta || !noteDirty) return;
    saveNoteFrom(ta);
  });

  // Guarantee the note is saved before leaving for checkout
  document.addEventListener('click', async (e) => {
    const checkout = e.target.closest('[data-cart-checkout]');
    if (!checkout) return;
    const ta = $('[data-cart-note]');
    if (ta && noteDirty) {
      e.preventDefault();
      await saveNoteFrom(ta);
      window.location = checkout.href;
    }
  });

  // Intercept product form submissions
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest(SELECTORS.productForm);
    if (!form) return;
    e.preventDefault();

    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
    const originalText = submitBtn ? submitBtn.querySelector('[data-cta-text]')?.textContent : null;

    if (submitBtn) {
      submitBtn.disabled = true;
      const textEl = submitBtn.querySelector('[data-cta-text]');
      if (textEl) textEl.textContent = T.adding;
    }

    try {
      const formData = new FormData(form);
      const item = await addToCart(formData);
      const cart = await fetchCart();
      renderDrawer(cart);
      updateHeaderCount(cart.item_count);
      openDrawer();
      document.dispatchEvent(new CustomEvent('aua:cart:add', { detail: { item, cart } }));
    } catch (err) {
      console.error('[AUA cart]', err);
      alert(err.message || T.addFail);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        const textEl = submitBtn.querySelector('[data-cta-text]');
        if (textEl && originalText) textEl.textContent = originalText;
      }
    }
  });

  // Re-fetch cart on bfcache restoration (avoids stale state)
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      fetchCart().then((cart) => {
        updateHeaderCount(cart.item_count);
        updateShipBar(cart.total_price);
      });
    }
  });
})();
